import React, { useState } from "react";
import {
  EMPLOYMENT_STATUS_OPTIONS,
  shouldDisableEmploymentDetails,
  normalizeEmploymentInfoForStatus,
} from "../../../../constants/membersConstants";
import StateCitySelect from "../../../StateCitySelect";
import { resolveStateCode } from "../../../../utils/stateUtils";
import { formatSSNInput } from "../../../../utils/validationHelpers";

const DETAIL_FIELDS = [
  { key: "occupation", label: "Occupation" },
  // industry removed from UI
  // { key: "industry", label: "Industry" },
  { key: "employer", label: "Employer" },
  { key: "title", label: "Job Title" },
  { key: "tin", label: "Employer TIN/EIN", format: "tin" },
];

const ADDRESS_FIELDS = [
  { key: "address_line_1", label: "Address Line 1" },
  { key: "city", label: "City" },
  { key: "province", label: "Province/State" },
  { key: "postal_code", label: "Postal Code" },
  { key: "country", label: "Country" },
];

const EmploymentInfoForm = ({
  householdData,
  fieldData,
  missingFields,
  entityId,
  onSave,
  onSaveAndExit,
  saving,
  nextLabel,
}) => {
  const member = fieldData || {};
  const existing = member.employment_info || {};
  const existingAddr = existing.address || {};

  const showStatus = !existing.status;
  const showDetails = !shouldDisableEmploymentDetails(existing.status || "Employed");
  const missingDetailFields = showDetails
    ? DETAIL_FIELDS.filter((f) => !existing[f.key])
    : [];
  const missingAddrFields = showDetails
    ? ADDRESS_FIELDS.filter((f) => !existingAddr[f.key])
    : [];
  const hasMissingLocation = missingAddrFields.some((f) =>
    ["city", "province", "country"].includes(f.key),
  );
  const missingNonLocationFields = missingAddrFields.filter(
    (f) => !["city", "province", "country"].includes(f.key),
  );

  const [formData, setFormData] = useState(() => {
    const initial = {};
    if (showStatus) initial.status = existing.status || "Employed";
    missingDetailFields.forEach((f) => { initial[f.key] = ""; });
    missingAddrFields.forEach((f) => {
      initial[f.key] = f.key === "country" ? "USA" : "";
    });
    return initial;
  });

  const currentShowDetails = showStatus
    ? !shouldDisableEmploymentDetails(formData.status || "Employed")
    : showDetails;

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = () => {
    const employment = { ...existing };
    if (showStatus) employment.status = formData.status;

    if (currentShowDetails) {
      missingDetailFields.forEach((f) => {
        employment[f.key] = formData[f.key] || null;
      });
      const addr = { ...(existing.address || {}) };
      missingAddrFields.forEach((f) => {
        const val = formData[f.key] || null;
        addr[f.key] = f.key === "province" && val ? resolveStateCode(val) : val;
      });
      employment.address = addr;
    }

    return {
      household_members: {
        [entityId]: {
          employment_info: normalizeEmploymentInfoForStatus(employment),
        },
      },
    };
  };

  const handleSubmit = (saveCallback) => {
    saveCallback(buildPayload());
  };

  const missingCount = (showStatus ? 1 : 0)
    + (currentShowDetails ? missingDetailFields.length + missingAddrFields.length : 0);

  const hasFieldsToShow = showStatus || (currentShowDetails && (missingDetailFields.length > 0 || missingAddrFields.length > 0));

  if (!hasFieldsToShow) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p className="text-sm">All employment fields have been filled.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Missing Information</h2>
        <span className="text-sm text-red-500 font-medium">
          ● {missingCount} Required field{missingCount !== 1 ? "s" : ""} missing
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        {showStatus && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employment Status <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.status}
              onChange={(e) => handleChange("status", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              {EMPLOYMENT_STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        )}

        {currentShowDetails && missingDetailFields.map((f) => (
          <div key={f.key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {f.label} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={f.format === "tin" ? formatSSNInput(formData[f.key]) : formData[f.key]}
              onChange={(e) =>
                handleChange(
                  f.key,
                  f.format === "tin"
                    ? e.target.value.replace(/\D/g, "").substring(0, 9)
                    : e.target.value,
                )
              }
              placeholder={f.format === "tin" ? "XX-XXXXXXX" : undefined}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        ))}
      </div>

      {currentShowDetails && missingAddrFields.length > 0 && (
        <>
          <h4 className="text-sm font-semibold text-gray-700 mt-6 mb-4">Work Address</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {hasMissingLocation && (
              <div className="md:col-span-2 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
                <StateCitySelect
                  countryValue={
                    formData.country !== undefined
                      ? formData.country
                      : existingAddr.country || "USA"
                  }
                  stateValue={
                    formData.province !== undefined
                      ? formData.province
                      : existingAddr.province || ""
                  }
                  cityValue={
                    formData.city !== undefined
                      ? formData.city
                      : existingAddr.city || ""
                  }
                  onCountryChange={(val) => handleChange("country", val)}
                  onStateChange={(val) => handleChange("province", val)}
                  onCityChange={(val) => handleChange("city", val)}
                  isEditing={true}
                  stateLabel="State"
                  cityLabel="City"
                />
              </div>
            )}
            {missingNonLocationFields.map((f) => (
              <div key={f.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {f.label} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData[f.key]}
                  onChange={(e) => handleChange(f.key, e.target.value)}
                  maxLength={f.key === "postal_code" ? 5 : undefined}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            ))}
          </div>
        </>
      )}

      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button onClick={() => handleSubmit(onSaveAndExit)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50">
          Save & Exit
        </button>
        <button onClick={() => handleSubmit(onSave)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2">
          {saving && <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
          {nextLabel || "Save & Next →"}
        </button>
      </div>
    </div>
  );
};

export default EmploymentInfoForm;
